import { Controller } from "@hotwired/stimulus"

const TAB = "pay-workbench-tab"
const HEIGHT = "pay-workbench-height"
const OPEN = "pay-workbench-open"
const MIN = 30
const MAX = 88

// The Workbench drawer: opens from the bar at the bottom (or ⌘J), loads
// the picked tab into its frame for the record on screen or for
// everything, and stays open across pages until closed. Its height is
// dragged from the grip and remembered.
export default class extends Controller {
  static targets = ["drawer", "frame", "tab", "scope", "toggle"]
  static values = { url: String, context: String }

  connect() {
    this.tab = this.read(localStorage, TAB) || "webhooks"
    this.range = "page"
    this.item = null
    this.onKey = this.onKey.bind(this)

    document.addEventListener("keydown", this.onKey)

    const height = Number(this.read(localStorage, HEIGHT))

    if (height) this.drawerTarget.style.setProperty("--h", `${height}vh`)
    const asked = new URLSearchParams(window.location.search).get("workbench")

    if (asked) {
      this.tab = asked
      this.show()
    } else if (this.read(sessionStorage, OPEN) === "1") {
      this.show()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKey)
  }

  get opened() {
    return this.element.dataset.drawer === "open"
  }

  toggle() {
    this.opened ? this.close() : this.show()
  }

  barClick(event) {
    if (event.target.closest(".devbar-icons")) return

    this.toggle()
  }

  open({ params }) {
    if (params.tab) this.tab = params.tab
    if (params.scope) this.range = params.scope

    this.item = params.item || null
    this.show()
  }

  select({ params }) {
    this.tab = params.tab
    this.item = null
    this.load()
  }

  pickScope({ params }) {
    this.range = params.scope
    this.item = null
    this.load()
  }

  show() {
    this.element.dataset.drawer = "open"
    this.drawerTarget.setAttribute("aria-hidden", "false")
    this.toggleTarget.setAttribute("aria-expanded", "true")
    this.write(sessionStorage, OPEN, "1")
    this.load()
  }

  close() {
    this.element.dataset.drawer = "closed"
    this.drawerTarget.setAttribute("aria-hidden", "true")
    this.toggleTarget.setAttribute("aria-expanded", "false")
    this.write(sessionStorage, OPEN, "0")
  }

  load() {
    if (!this.tabTargets.some((tab) => tab.dataset.tab === this.tab)) this.tab = "webhooks"

    this.write(localStorage, TAB, this.tab)

    this.tabTargets.forEach((tab) => tab.setAttribute("aria-selected", String(tab.dataset.tab === this.tab)))
    this.scopeTargets.forEach((scope) => scope.setAttribute("aria-pressed", String(scope.dataset.scope === this.range)))

    const url = new URL(this.urlValue, window.location.origin)

    url.searchParams.set("tab", this.tab)
    if (this.contextValue) url.searchParams.set("context", this.contextValue)
    if (this.contextValue && this.range === "all") url.searchParams.set("scope", "all")
    if (this.item) url.searchParams.set("item", this.item)

    const src = url.pathname + url.search

    if (this.frameTarget.getAttribute("src") === src) {
      this.frameTarget.reload?.()
    } else {
      this.frameTarget.src = src
    }
  }

  onKey(event) {
    const typing = event.target.closest?.("input, textarea, select, [contenteditable]")

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "j") {
      event.preventDefault()
      this.toggle()
    } else if (event.key === "Escape" && !typing && this.opened && !document.querySelector("dialog[open], [data-disclosure-target=panel]:not([hidden])")) {
      this.close()
    }
  }

  startResize(event) {
    if (event.button !== 0 || window.matchMedia("(max-width: 760px)").matches) return

    event.preventDefault()

    const move = (moved) => {
      const vh = ((window.innerHeight - moved.clientY) / window.innerHeight) * 100

      this.height = Math.round(Math.min(MAX, Math.max(MIN, vh)))
      this.drawerTarget.style.setProperty("--h", `${this.height}vh`)
    }

    const up = () => {
      document.removeEventListener("pointermove", move)
      document.body.classList.remove("select-none", "cursor-ns-resize")

      if (this.height) this.write(localStorage, HEIGHT, String(this.height))
    }

    document.body.classList.add("select-none", "cursor-ns-resize")
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", up, { once: true })
  }

  read(storage, key) {
    try {
      return storage.getItem(key)
    } catch (e) {
      return null
    }
  }

  write(storage, key, value) {
    try {
      storage.setItem(key, value)
    } catch (e) {
      console.warn("workbench state not saved", e)
    }
  }
}
